import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { getCryptoDecimals } from 'src/core/const';
import './PaymentIssuesCard.css';

/**
 * Payment Issues Card Component
 * Displays counts and totals for underfunded, overfunded, and unexpected payments
 * Clicking navigates to filtered transaction list
 */
export function PaymentIssuesCard({ paymentIssues, loading, isSolventAdmin = false }) {
  const navigate = useNavigate();

  function formatCurrency(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value || 0);
  }

  function handleIssueClick(query) {
    // Navigate to transactions page with the given filter query
    navigate(`/solventpay/transactions?${query}`);
  }

  function formatLateDuration(seconds) {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `avg ${mins}m ${secs}s late`;
    }
    return `avg ${secs}s late`;
  }

  // Unexpected payments are a Solvent-internal reconciliation concern;
  // FI/Merchant/Reseller users never see the count, value, or crypto.
  const unexpectedCount = isSolventAdmin
    ? (paymentIssues?.unexpected_payments?.count || 0)
    : 0;

  // Don't show if no issues
  const totalIssues =
    (paymentIssues?.underfunded?.count || 0) +
    (paymentIssues?.overfunded?.count || 0) +
    unexpectedCount +
    (paymentIssues?.late_payments?.count || 0);

  if (loading) {
    return (
      <div className="payment-issues-card loading">
        <div className="payment-issues-header">
          <FontAwesomeIcon icon={['fal', 'exclamation-triangle']} className="header-icon" />
          <span>Payment Issues</span>
        </div>
        <div className="payment-issues-loading">Loading...</div>
      </div>
    );
  }

  if (totalIssues === 0) {
    return null; // Don't render if no issues
  }

  return (
    <div className="payment-issues-card">
      <div className="payment-issues-header">
        <FontAwesomeIcon icon={['fal', 'exclamation-triangle']} className="header-icon warning" />
        <span>Payment Issues</span>
        <span className="issue-count-badge">{totalIssues}</span>
      </div>

      <div className="payment-issues-content">
        {/* Underfunded */}
        {paymentIssues?.underfunded?.count > 0 && (
          <div
            className="issue-row underfunded clickable"
            onClick={() => handleIssueClick('discrepancy=underfunded')}
          >
            <div className="issue-info">
              <FontAwesomeIcon icon={['fal', 'arrow-down']} className="issue-icon error" />
              <span className="issue-label">Underfunded</span>
            </div>
            <div className="issue-stats">
              <span className="issue-count">{paymentIssues.underfunded.count}</span>
              <span className="issue-amount error">
                -{formatCurrency(paymentIssues.underfunded.total_shortfall_usd)}
              </span>
            </div>
          </div>
        )}

        {/* Overfunded */}
        {paymentIssues?.overfunded?.count > 0 && (
          <div
            className="issue-row overfunded clickable"
            onClick={() => handleIssueClick('discrepancy=overfunded')}
          >
            <div className="issue-info">
              <FontAwesomeIcon icon={['fal', 'arrow-up']} className="issue-icon warning" />
              <span className="issue-label">Overfunded</span>
            </div>
            <div className="issue-stats">
              <span className="issue-count">{paymentIssues.overfunded.count}</span>
              <span className="issue-amount warning">
                +{formatCurrency(paymentIssues.overfunded.total_overage_usd)}
              </span>
            </div>
          </div>
        )}

        {/* Unexpected Payments — Solvent Admin only (crypto values exposed).
            Not clickable: unexpected payments have no session rows to drill into. */}
        {isSolventAdmin && paymentIssues?.unexpected_payments?.count > 0 && (
          <div className="issue-row unexpected">
            <div className="issue-info">
              <FontAwesomeIcon icon={['fal', 'question-circle']} className="issue-icon info" />
              <span className="issue-label">Unexpected</span>
            </div>
            <div className="issue-stats">
              <span className="issue-count">{paymentIssues.unexpected_payments.count}</span>
              <span className="issue-amount info">
                <span className="total-usd">
                  {formatCurrency(paymentIssues.unexpected_payments.total_usd || 0)}
                </span>
                {paymentIssues.unexpected_payments.totals_by_crypto && (
                  <span className="crypto-breakdown">
                    {' ('}
                    {Object.entries(paymentIssues.unexpected_payments.totals_by_crypto)
                      .map(([crypto, data]) => (
                        <span key={crypto} className="crypto-group">
                          {parseFloat(data.amount).toFixed(getCryptoDecimals(crypto))} {crypto}
                        </span>
                      ))
                      .reduce((prev, curr, i) => i === 0 ? [curr] : [...prev, <span key={`sep-${i}`}>, </span>, curr], [])}
                    {')'}
                  </span>
                )}
              </span>
            </div>
          </div>
        )}

        {/* Late Payments */}
        {paymentIssues?.late_payments?.count > 0 && (
          <div
            className="issue-row late clickable"
            onClick={() => handleIssueClick('late=true')}
          >
            <div className="issue-info">
              <FontAwesomeIcon icon={['fal', 'clock']} className="issue-icon late" />
              <span className="issue-label">Late Payments</span>
            </div>
            <div className="issue-stats">
              <span className="issue-count">{paymentIssues.late_payments.count}</span>
              <span className="issue-amount late">
                {formatCurrency(paymentIssues.late_payments.total_usd)}
                {paymentIssues.late_payments.avg_late_seconds > 0 && (
                  <span className="late-avg"> ({formatLateDuration(paymentIssues.late_payments.avg_late_seconds)})</span>
                )}
              </span>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
